/**
 * Tags Zustand Store
 *
 * Manages all state for Account Tags (CRM tagging) including:
 * - Tag list
 * - Tag CRUD operations
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  AccountTag,
  CreateTagDto,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface TagsError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface TagsState {
  // Tags List
  tags: AccountTag[];
  isLoadingList: boolean;
  listError: TagsError | null;

  // Action States
  isPerformingAction: boolean;
  actionError: TagsError | null;

  // Actions - List
  loadTags: () => Promise<void>;
  refreshTags: () => Promise<void>;

  // Actions - CRUD
  createTag: (dto: CreateTagDto) => Promise<string | null>;
  deleteTag: (id: string) => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Tags List
  tags: [] as AccountTag[],
  isLoadingList: false,
  listError: null as TagsError | null,

  // Action States
  isPerformingAction: false,
  actionError: null as TagsError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useTagsStore = create<TagsState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadTags: async () => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const response = await api.get<AccountTag[]>(API_ENDPOINTS.SUPER_ADMIN.TAGS.LIST);

      set({
        tags: response,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_TAGS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load tags',
        },
      });
    }
  },

  refreshTags: async () => {
    await get().loadTags();
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createTag: async (dto: CreateTagDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<AccountTag>(API_ENDPOINTS.SUPER_ADMIN.TAGS.CREATE, dto);

      // Add to local state
      set((state) => ({
        tags: [...state.tags, response],
        isPerformingAction: false,
      }));

      return response.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_TAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create tag',
        },
      });
      return null;
    }
  },

  deleteTag: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.TAGS.DELETE(id));

      // Remove from local state
      set((state) => ({
        tags: state.tags.filter((t) => t.id !== id),
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_TAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete tag',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({ listError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get total tags count */
export const useTagsCount = () => {
  return useTagsStore((state) => state.tags.length);
};

/** Get tag by ID */
export const useTagById = (id: string) => {
  return useTagsStore((state) =>
    state.tags.find((t) => t.id === id)
  );
};

/** Check if any loading state is active */
export const useTagsLoading = () => {
  const isLoadingList = useTagsStore((state) => state.isLoadingList);
  const isPerformingAction = useTagsStore((state) => state.isPerformingAction);
  return isLoadingList || isPerformingAction;
};

/** Get tags sorted by account count */
export const useTagsByUsage = () => {
  return useTagsStore((state) =>
    [...state.tags].sort((a, b) => b.accountCount - a.accountCount)
  );
};
